import { pick } from 'lodash'
import { defineStore, acceptHMRUpdate } from 'pinia'
import { Unsubscribe, TwitterAuthProvider, getAuth, signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth'
import { collection, doc, getDoc, setDoc } from 'firebase/firestore'
import { useFireStore } from '@/stores/firebase'
import { UserInfo } from '@/types/pricolle'


const STORE_ID = 'UserStore'


/**
 * onAuthStateChanged() の返値格納先
 * @inner
 */
let unsubscribeAuthStateChange: Unsubscribe | undefined


/**
 * Firebase Auth のユーザ情報を UserInfo 型へ変換する
 * @inner
 */
function parseUser(user: UserInfo | null): UserInfo {
  return {
    uid: user?.uid || '',
    displayName: user?.displayName || '',
    photoURL: user?.photoURL || '',
    isAdmin: !!user?.isAdmin,
  }
}


export const useStore = defineStore(STORE_ID, {
  state() {
    return {
      /**
       * Firebase Auth でログインした「自分自身」
       */
      myself: { uid: '', displayName: '', photoURL: '' } as UserInfo,

      /**
       * 現在の表示対象ユーザ
       */
      targetUser: { uid: '', displayName: '', photoURL: '' } as UserInfo,
    }
  },

  getters: {
    /**
     * Firebase Auth でログインした「自分自身」がいれば true
     */
    isLoggedIn(): boolean {
      return !!this.myself.uid
    },

    /**
     * Firebase Auth でログインした「自分自身」が管理者なら true
     */
    isAdmin(): boolean {
      return this.myself.isAdmin
    },

    /**
     * 表示対象のユーザが設定されていれば true
     */
    hasTargetUser(): boolean {
      return !!this.targetUser.uid
    },

    /**
     * 表示対象のユーザが自分自身ならば true
     */
    targetUserIsMyself(): boolean {
      return this.hasTargetUser && this.targetUser.uid === this.myself.uid
    },

    /**
     * 表示対象のユーザの uid を含む（かもしれない） SearchParams
     */
    queryStringWithTargetUid(): string {
      const uid = this.targetUser.uid
      const params = new URLSearchParams(location.search)
      uid ? params.set('uid', uid) : params.delete('uid')
      const search = params.toString()
      return search ? `?${search}` : ''
    },
  },

  actions: {
    /**
     * Firebase Auth でログインする
     */
    async login(): Promise<void> {
      const prompt = `[${STORE_ID}] login`

      const auth = getAuth()
      const provider = new TwitterAuthProvider()
      const result = await signInWithPopup(auth, provider)
        .catch(error => {
          const { code, message } = error
          const credential = TwitterAuthProvider.credentialFromError(error)

          // eslint-disable-next-line no-console
          console.error(`${prompt}: failed:`, code, message, credential)
          return null
        })

      if (!result?.user) {
        this.resetMyself()
      }
      else {
        const credential = TwitterAuthProvider.credentialFromResult(result)
        const user = pick(result.user, [ 'uid', 'displayName', 'photoURL' ]) as UserInfo
        const myself = parseUser(user)
        myself.isAdmin = !!(await this.fetchUserInfo(myself.uid))?.isAdmin
        this.$patch({ myself })

        // eslint-disable-next-line no-console
        console.info(`${prompt}: success:`, myself, credential)

        if (await this.storeUserInfoIfLoggedIn()) {
          this.setMyselfAsTargetUserIfNeeded()
        }
        else {
          // Firebase Auth から得たユーザ情報を Firestore へ格納しようとして
          // それに失敗する場合は、ログインが許可されていないものとみなす。
          // NOTE: これは暫定措置。Firestore のルールが新規の情報格納を禁じている。
          alert('ログインできません')
          this.logout()
        }
      }
    },

    /**
     * Firebase Auth からログアウトする
     */
    async logout(): Promise<void> {
      const prompt = `[${STORE_ID}] logout`

      const auth = getAuth()
      const result = await signOut(auth)
        .catch(async error => {
          // eslint-disable-next-line no-console
          console.error(`${prompt}: failed:`, error)
          return 'error'
        })

      // ログアウト成功時の result は undefined になっている
      if (result === undefined) {
        this.unsetMyselfAsTargetUserIfNeeded()
        this.resetMyself()
        unsubscribeAuthStateChange = undefined

        // eslint-disable-next-line no-console
        console.info(`${prompt}: success.`)
      }
    },

    /**
     * Firebase Auth から現在ログイン中のユーザ（自分自身）の情報を取り込む
     */
    async fetchAuthorizedUser(): Promise<void> {
      const prompt = `[${STORE_ID}] fetchAuthorizedUser`
      const auth = getAuth()

      await new Promise(resolve => {
        // 初めて getAuth() した直後はログイン状態の判定がまだできない。
        // そこで、ログイン状態の変化をまずは監視して、ログイン状態の確定判断を下す。
        if (!unsubscribeAuthStateChange) {
          unsubscribeAuthStateChange = onAuthStateChanged(auth, () => {
            unsubscribeAuthStateChange?.()
            resolve(1)
          })
        }
        else {
          resolve(0)
        }
      })

      const user = pick(auth.currentUser, [ 'uid', 'displayName', 'photoURL' ]) as UserInfo
      const myself = parseUser(user)
      myself.isAdmin = !!(await this.fetchUserInfo(myself.uid))?.isAdmin
      this.$patch({ myself })

      // eslint-disable-next-line no-console
      console.info(`${prompt}:`, this.isLoggedIn ? myself : 'not authorized.')

      this.setMyselfAsTargetUserIfNeeded()
      await this.storeUserInfoIfLoggedIn()
    },

    /**
     * uid で指名したユーザの情報を Firestore から取得する
     */
    async fetchUserInfo(uid: string): Promise<UserInfo | undefined> {
      if (!uid) return undefined

      const prompt = `[${STORE_ID}] fetchUserInfo`
      const db = useFireStore()
      const ref = doc(db, 'users', uid)
      const snapshot = await getDoc(ref)
        // eslint-disable-next-line no-console
        .catch(error => console.error(`${prompt}:`, error))

      return snapshot?.exists()
        ? parseUser(snapshot.data() as UserInfo)
        : undefined
    },

    /**
     * 自分自身がログイン中なら、
     * Store が保持している自分のユーザ情報を Firestore へ格納する
     */
    async storeUserInfoIfLoggedIn(): Promise<boolean> {
      if (!this.isLoggedIn) return false

      const prompt = `[${STORE_ID}] storeUserInfoIfLoggedIn`
      const db = useFireStore()
      const ref = collection(db, 'users')
      const { myself } = this
      const result = await setDoc(doc(ref, myself.uid), myself)
        .catch(error => {
          // eslint-disable-next-line no-console
          console.error(`${prompt}:`, error)
          return 'error'
        })
      return result !== 'error'
    },

    /**
     * 表示対象のユーザを変更する
     */
    async setTargetUser(uid: string): Promise<void> {
      if (this.targetUser.uid === uid) return

      const prompt = `[${STORE_ID}] setTargetUser`
      let message = ''

      const urlSearchParams = new URLSearchParams(location.search)
      const targetUser = await this.fetchUserInfo(uid)

      if (targetUser) {
        this.$patch({ targetUser })
        urlSearchParams.set('uid', uid)
        message = `user "${uid}" became a target user.`
      }
      else {
        this.resetTargetUser()
        urlSearchParams.delete('uid')
        message = 'nobody is a target user.'
      }

      const data = history.state  // Vue Router が必要とするらしい
      const search = urlSearchParams.toString()
      const path = location.pathname
      const url = search ? `${path}?${search}` : path

      history.replaceState(data, '', url)

      // eslint-disable-next-line no-console
      message && console.info(`${prompt}: ${message}`)
    },

    /**
     * 自分自身がログイン中かつ、表示対象のユーザが未設定なら、
     * 自分を表示対象のユーザにする。
     */
    setMyselfAsTargetUserIfNeeded(): void {
      const { myself, hasTargetUser } = this
      if (myself.uid && !hasTargetUser) {
        this.setTargetUser(myself.uid)
      }
    },

    /**
     * 自分自身がログイン中かつ、表示対象のユーザになっているなら、
     * 自分を表示対象のユーザから外す
     */
    unsetMyselfAsTargetUserIfNeeded(): void {
      const { myself, targetUser } = this
      if (myself.uid === targetUser.uid) {
        this.setTargetUser('')
      }
    },

    /**
     * 「自分自身」の情報を初期化する
     */
    resetMyself() {
      const myself = parseUser(null)
      this.$patch({ myself })
    },

    /**
     * 「現在の表示対象ユーザ」の情報を初期化する
     */
    resetTargetUser() {
      const targetUser = parseUser(null)
      this.$patch({ targetUser })
    },
  },
})


// Hot Module Replacement
// NOTE: Pinia の HMR について→ https://pinia.vuejs.org/cookbook/hot-module-replacement.html
// FIXME: しかし動作しない感じ→ https://github.com/vuejs/pinia/discussions/1072
const hot = /* import.meta.hot */ import.meta.webpackHot
if (hot) {
  hot.accept(acceptHMRUpdate(useStore, hot))
}

